import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, UploadCloud } from "lucide-react";
import api from "../lib/api";

const AdminProfile = () => {
  const [name, setName] = useState("Ari");
  const [image, setImage] = useState("");
  const [personalZip, setPersonalZip] = useState("");
  const [storyHeading, setStoryHeading] = useState("");
  const [story, setStory] = useState("");
  const [highlights, setHighlights] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);
  const [storyImage, setStoryImage] = useState("");
  const [storyFile, setStoryFile] = useState(null);
  const [storyPreview, setStoryPreview] = useState("");
  const storyFileRef = useRef(null);

  useEffect(() => {
    api
      .get("/profile")
      .then((r) => {
        setName(r.data.name || "Ari");
        setImage(r.data.image || "");
        setStoryImage(r.data.story_image || "");
        setPersonalZip(r.data.personal_zip || "");
        setStoryHeading(r.data.story_heading || "");
        setStory(r.data.story || "");
        setHighlights(r.data.highlights || "");
        setLoaded(true);
      })
      .catch(() => {});
  }, []);

  const pick = (f) => {
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : "");
  };

  const pickStory = (f) => {
    setStoryFile(f || null);
    setStoryPreview(f ? URL.createObjectURL(f) : "");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!loaded) return;
    setBusy(true);
    try {
      let imageUrl = image;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/admin/upload", fd);
        imageUrl = up.url;
      }
      let storyImageUrl = storyImage;
      if (storyFile) {
        const fd2 = new FormData();
        fd2.append("file", storyFile);
        const { data: up2 } = await api.post("/admin/upload", fd2);
        storyImageUrl = up2.url;
      }
      const { data } = await api.put("/profile", {
        name,
        image: imageUrl,
        story_image: storyImageUrl,
        personal_zip: personalZip,
        story_heading: storyHeading,
        story,
        highlights,
      });
      setImage(data.image);
      setStoryImage(data.story_image || "");
      setPersonalZip(data.personal_zip || "");
      setFile(null);
      setPreview("");
      setStoryFile(null);
      setStoryPreview("");
      if (fileRef.current) fileRef.current.value = "";
      if (storyFileRef.current) storyFileRef.current.value = "";
      toast.success("Saved — visitors will see this at the top of the homepage.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="h-fit rounded-3xl border border-line bg-white p-8" data-testid="admin-profile-form">
      <h3 className="font-display text-2xl font-medium text-ink">Your photo & name</h3>
      <p className="mt-2 text-sm leading-relaxed text-inkSoft">
        A little portrait at the top of the homepage, so pet owners know exactly who's showing up at their
        door.
      </p>

      <div className="mt-6 flex items-center gap-5">
        <span className="relative inline-flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-sand ring-2 ring-marmalade ring-offset-2 ring-offset-white">
          {preview ? (
            <img src={preview} alt="Preview" className="h-full w-full object-cover" data-testid="profile-preview" />
          ) : image ? (
            <img src={image} alt="Ari" className="h-full w-full object-cover" data-testid="portrait-preview" />
          ) : (
            <Camera className="h-8 w-8 text-marmalade" />
          )}
        </span>
        <label className="cursor-pointer">
          <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-sand">
            <UploadCloud className="h-4 w-4 text-marmalade" />
            Choose a photo
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => pick(e.target.files?.[0])}
            data-testid="admin-profile-file"
            className="hidden"
          />
        </label>
      </div>
      {file && <p className="mt-3 text-xs text-inkSoft">New photo ready — hit save to publish it.</p>}

      <div className="mt-6 border-t border-line pt-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Meet Ari photo</span>
        <p className="mt-1 text-xs leading-relaxed text-inkSoft">
          A candid you-and-pet moment for the Meet Ari section — something different from the portrait above
          feels extra personal.
        </p>
        <div className="mt-4 flex items-center gap-5">
          <span className="relative inline-flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[999px_999px_28px_28px] bg-sand ring-2 ring-marmalade ring-offset-2 ring-offset-white">
            {storyPreview ? (
              <img src={storyPreview} alt="Meet Ari preview" className="h-full w-full object-cover" data-testid="story-image-preview" />
            ) : storyImage ? (
              <img src={storyImage} alt="Meet Ari" className="h-full w-full object-cover" data-testid="story-image-preview" />
            ) : (
              <Camera className="h-8 w-8 text-marmalade" />
            )}
          </span>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-sand">
              <UploadCloud className="h-4 w-4 text-marmalade" />
              Choose the story photo
            </span>
            <input
              ref={storyFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => pickStory(e.target.files?.[0])}
              data-testid="admin-story-file"
              className="hidden"
            />
          </label>
        </div>
        {storyFile && <p className="mt-3 text-xs text-inkSoft">New story photo ready — hit save to publish it.</p>}
      </div>

      <div className="mt-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Your name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ari"
          data-testid="admin-profile-name"
          className="mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
        />
      </div>

      <div className="mt-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Your zip code (center of your service radius)</span>
        <input
          value={personalZip}
          onChange={(e) => setPersonalZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="e.g. 11201"
          inputMode="numeric"
          data-testid="admin-profile-zip"
          className="mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-inkSoft">
          Saving this sets it as the center of your service area below — then just flip the toggle and choose
          your miles.
        </p>
      </div>

      <div className="mt-6 border-t border-line pt-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">Your "Meet Ari" story</p>
        <div className="mt-3 space-y-4">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Section heading (optional)</span>
            <input
              value={storyHeading}
              onChange={(e) => setStoryHeading(e.target.value)}
              placeholder="The human behind the happy tails"
              data-testid="admin-profile-story-heading"
              className="mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Your story (blank line = new paragraph)</span>
            <textarea
              value={story}
              onChange={(e) => setStory(e.target.value)}
              rows={6}
              placeholder={"Ari's Happy Tails is me — Ari. Not an app, not a rotation of strangers: when you book, I'm the one at your door.\n\nI keep every walk, meal and nap exactly the way your pet knows them…"}
              data-testid="admin-profile-story"
              className="mt-2 w-full resize-none rounded-2xl border border-line bg-cream px-5 py-4 text-base leading-relaxed text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Highlights (one per line)</span>
            <textarea
              value={highlights}
              onChange={(e) => setHighlights(e.target.value)}
              rows={4}
              placeholder={"Overnight house sitting\nBoarding in my own home\nDog walking — 30 or 60 minutes"}
              data-testid="admin-profile-highlights"
              className="mt-2 w-full resize-none rounded-2xl border border-line bg-cream px-5 py-4 text-base leading-relaxed text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={busy || !loaded}
        data-testid="admin-profile-save"
        className="mt-6 w-full rounded-full bg-forest py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink disabled:opacity-60"
      >
        {busy ? "Saving…" : loaded ? "Save profile" : "Loading your details…"}
      </button>
    </form>
  );
};

export default AdminProfile;
